import React, { useState } from "react";
import { IconButton, Tooltip } from "@mui/material";
import { CheckmarkOutlinedIcon, CheckmarkContainedIcon } from "assets";
import { useNotify, useReviewScorecardDataCell, useUnreviewScorecardDataCell } from "hooks";
import { DeleteDialog } from "components/dialog/delete-dialog";

interface ReviewCellValueButtonProps {
    dataId: number | null;
    isReviewed: boolean;
    disabled?: boolean;
}

export const ReviewCellValueButton: React.FC<ReviewCellValueButtonProps> = ({
    dataId,
    isReviewed,
    disabled = false,
}) => {
    const notify = useNotify();
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

    const { mutateAsync: reviewScorecardDataCell, isLoading: isReviewing } = useReviewScorecardDataCell();
    const { mutateAsync: unreviewScorecardDataCell, isLoading: isUnreviewing } = useUnreviewScorecardDataCell();

    const handleReviewCell = async () => {
        if (!dataId) {
            notify.error('No data ID available for review');
            return;
        }

        try {
            await reviewScorecardDataCell(dataId);
        } catch (error: any) {
            notify.error(error?.response?.data || 'Failed to review cell value');
        }
    };

    const handleUnreviewCell = async () => {
        if (!dataId) {
            notify.error('No data ID available for unreview');
            return;
        }

        try {
            await unreviewScorecardDataCell(dataId);
        } catch (error: any) {
            notify.error(error?.response?.data || 'Failed to unreview cell value');
        }
    }

    const onReviewIconClick = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        if (isReviewed) {
            setDeleteDialogOpen(true);
        } else {
            handleReviewCell();
        }
    }

    const IconComponent = isReviewed ? CheckmarkContainedIcon : CheckmarkOutlinedIcon;
    const iconColor = isReviewed ? "#46A314" : (disabled ? "#9CA3AF" : "#054254");

    return (
        <>
            <Tooltip arrow placement="top" title={isReviewed ? "'Unreview' data point" : "Authorized users can mark entries as 'Reviewed'."}>
                <IconButton
                    sx={{
                        width: "20px",
                        height: "20px",
                        padding: 0,
                        "&:hover": {
                            backgroundColor: "rgba(5, 66, 84, 0.1)",
                        },
                    }}
                    onClick={onReviewIconClick}
                    disabled={disabled || isReviewing || isUnreviewing}
                >
                    <IconComponent style={{
                        width: "17px",
                        height: "13px",
                        color: iconColor
                    }} />
                </IconButton>
            </Tooltip>
            {deleteDialogOpen && (
                <DeleteDialog
                    isOpen={deleteDialogOpen}
                    onCancel={() => setDeleteDialogOpen(false)}
                    onDeleteClick={handleUnreviewCell}
                    target="unreviewCell"
                    title="Unreview cell?"
                    confirmButtonDisabled={isUnreviewing}
                    confirmTitle={isUnreviewing ? "Unreviewing..." : "Confirm"}
                />
            )}
        </>
    );
};
